<template>
  <div class="p-6">
    <!-- Header: Title and Controls -->
    <div class="flex flex-col md:flex-row md:justify-between items-start md:items-center gap-4 mb-4">
      <!-- Left Group: Title & Refresh -->
      <div class="flex items-center space-x-3 flex-shrink-0">
      <h1 class="text-3xl font-bold text-black">Leaderboard</h1>
        <button
          @click="fetchDriversData"
          :disabled="loading"
          title="Refresh Data"
          class="p-1.5 rounded-full text-gray-600 hover:bg-primary hover:text-white disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <!-- Refresh Icon SVG -->
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
        </button>
      </div>

      <!-- Controls Group -->
      <div class="flex flex-col md:flex-row md:items-center gap-3 w-full md:w-auto">
        <!-- Search Input (Always Visible, Order 1) -->
        <div class="relative flex-grow md:flex-grow-0 w-full md:w-60 order-1">
           <input
             type="text"
             v-model="searchQuery"
             placeholder="Search..."
             class="pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-full focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary w-full"
           />
           <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
             <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
               <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
             </svg>
           </div>
        </div>

        <!-- Mobile Wrapper for Toggle and Picker (Order 2 on mobile, md:hidden) -->
        <div class="flex justify-between items-center w-full md:hidden order-2">
          <!-- Time Period Toggle Switch -->

          <!-- Pickers for Mobile -->
          <VueDatePicker
            v-if="timePeriod === 'daily'"
            v-model="selectedDate"
            format="dd/MM/yyyy"
            :enable-time-picker="false"
            :max-date="new Date()"
            auto-apply
            :clearable="false"
            placeholder="Select Date"
            class="w-40"
            input-class-name="dp-custom-input"
          />
          <div class="monthly-picker-wrapper" v-if="timePeriod === 'mtd'">
            <VueDatePicker
              v-model="selectedMonth"
              month-picker
              format="MM/yyyy"
              :max-date="new Date()"
              auto-apply
              :clearable="false"
              placeholder="Select Month"
              class="w-40"
              input-class-name="dp-custom-input"
            />
          </div>

          <div class="flex items-center space-x-2">
              <span class="text-sm font-medium text-gray-700" :class="{'text-primary font-semibold': timePeriod === 'daily'}">Daily</span>
              <button
                type="button"
                @click="toggleTimePeriod"
                :class="timePeriod === 'mtd' ? 'bg-primary' : 'bg-gray-200'"
                class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                role="switch"
                :aria-checked="timePeriod === 'mtd'"
              >
                <span class="sr-only">Use setting</span>
                <span
                  aria-hidden="true"
                  :class="timePeriod === 'mtd' ? 'translate-x-5' : 'translate-x-0'"
                  class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
                ></span>
              </button>
              <span class="text-sm font-medium text-gray-700" :class="{'text-primary font-semibold': timePeriod === 'mtd'}">Monthly</span>
          </div>

        </div>

        <!-- Desktop Toggle Switch (Order 3 on desktop, hidden md:flex) -->
        <div class="hidden md:flex items-center space-x-2 order-3">
            <span class="text-sm font-medium text-gray-700" :class="{'text-primary font-semibold': timePeriod === 'daily'}">Daily</span>
            <button
              type="button"
              @click="toggleTimePeriod"
              :class="timePeriod === 'mtd' ? 'bg-primary' : 'bg-gray-200'"
              class="relative inline-flex flex-shrink-0 h-6 w-11 border-2 border-transparent rounded-full cursor-pointer transition-colors ease-in-out duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
              role="switch"
              :aria-checked="timePeriod === 'mtd'"
            >
              <span class="sr-only">Use setting</span>
              <span
                aria-hidden="true"
                :class="timePeriod === 'mtd' ? 'translate-x-5' : 'translate-x-0'"
                class="pointer-events-none inline-block h-5 w-5 rounded-full bg-white shadow transform ring-0 transition ease-in-out duration-200"
              ></span>
            </button>
            <span class="text-sm font-medium text-gray-700" :class="{'text-primary font-semibold': timePeriod === 'mtd'}">Monthly</span>
        </div>

        <!-- Desktop Date Picker (Order 2 on desktop, hidden md:block) -->
        <VueDatePicker
          v-if="timePeriod === 'daily'"
          v-model="selectedDate"
          format="dd/MM/yyyy"
          :enable-time-picker="false"
          :max-date="new Date()"
          auto-apply
          :clearable="false"
          placeholder="Select Date"
          class="w-40 hidden md:block order-2"
          input-class-name="dp-custom-input"
        />

        <!-- Desktop Month Picker (Order 2 on desktop, hidden md:block) -->
        <div class="monthly-picker-wrapper w-40 hidden md:block order-2" v-if="timePeriod === 'mtd'">
          <VueDatePicker
            v-model="selectedMonth"
            month-picker
            format="MM/yyyy"
            :max-date="new Date()"
            auto-apply
            :clearable="false"
            placeholder="Select Month"
            class="w-full"
            input-class-name="dp-custom-input"
          />
        </div>

      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="text-center py-20 flex justify-center items-center">
      <svg class="animate-spin h-12 w-12 text-teal-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
      </svg>
    </div>

    <!-- Error State -->
    <div v-else-if="apiError" class="text-center py-20 text-red-600">
      <p class="text-lg">Error loading leaderboard data. Please try again later.</p>
    </div>

    <!-- Content Area (Table & Mobile View) -->
    <div v-else>
    <!-- Container for Table -->
      <Transition name="fade" mode="out-in">
        <div class="table-container hidden md:block">
      <table class="min-w-full table-auto">
            <thead class="bg-gray-50">
              <tr>
                <!-- Rank (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                  <button type="button" @click="sortBy('rank')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" /></svg>
                    <span class="text-sm">Rank</span>
                    <!-- Sort Icons -->
                    <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'rank' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'rank' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                    </span>
                  </button>
                </th>
                <!-- Cruiser Name (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                   <button type="button" @click="sortBy('cruiser_name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" /></svg>
                     <span class="text-sm">Cruiser</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'cruiser_name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'cruiser_name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
                </th>
                <!-- Mobile Number (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                   <button type="button" @click="sortBy('mobile_number')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                     <span class="text-sm">Mobile</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'mobile_number' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'mobile_number' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
                </th>
                <!-- Car Model (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                   <button type="button" @click="sortBy('car_model')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                     <span class="text-sm">Model</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'car_model' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'car_model' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
                </th>
                <!-- Plate Number (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                   <button type="button" @click="sortBy('plate_number')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                     <span class="text-sm">Plate</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'plate_number' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'plate_number' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
                </th>
                <!-- Screen Name (Sortable, Daily Only) -->
            <th
              v-if="timePeriod === 'daily'"
                  scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200"
                >
                   <button type="button" @click="sortBy('screen_name')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                     <span class="text-sm">Screen</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'screen_name' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'screen_name' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
                </th>
                <!-- Percentage (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                   <button type="button" @click="sortBy('percentage')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>
                     <span class="text-sm">Percent</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'percentage' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'percentage' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
                </th>
                <!-- User Time (Sortable) -->
                <th scope="col" class="py-3 px-3 font-semibold text-gray-600 border-r border-gray-200">
                  <button type="button" @click="sortBy('user_time')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                     <span class="text-sm">Time</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'user_time' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'user_time' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                  </button>
                </th>
                 <!-- Last Seen (Sortable) -->
                 <th scope="col" class="py-3 px-3 font-semibold text-gray-600">
                   <button type="button" @click="sortBy('last_seen')" class="flex items-center space-x-1.5 w-full text-left group focus:outline-none">
                     <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                     <span class="text-sm">Last Seen</span>
                     <span class="opacity-50 group-hover:opacity-100">
                       <svg v-if="sortColumn === 'last_seen' && sortDirection === 'asc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M5 15l7-7 7 7" /></svg>
                       <svg v-else-if="sortColumn === 'last_seen' && sortDirection === 'desc'" xmlns="http://www.w3.org/2000/svg" class="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3"><path stroke-linecap="round" stroke-linejoin="round" d="M19 9l-7 7-7-7" /></svg>
                       <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-3 w-3 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4" /></svg>
                     </span>
                   </button>
            </th>
          </tr>
        </thead>
            <tbody class="bg-white divide-y divide-gray-200">
              <!-- Empty State Row -->
              <tr v-if="!loading && !apiError && !processedDrivers.length">
                <td :colspan="timePeriod === 'daily' ? 9 : 8" class="px-6 py-10 text-center text-sm text-gray-500">
                    No drivers found matching your criteria.
                </td>
              </tr>
              <!-- Data Rows -->
              <tr
                v-for="(driver, index) in processedDrivers"
                :key="driver.id || driver.mobile_number || index"
                :class="getRankRowClass(driver.rank)"
              >
                <td class="py-3 px-4 whitespace-nowrap text-sm font-medium text-gray-900 border-b border-gray-200 border-r">{{ driver.rank }}</td>
                <td class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200 border-r">
                  <span class="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{{ driver.cruiser_name }}</span>
                </td>
                <td class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 relative group cursor-pointer border-b border-gray-200 border-r" @click="copyToClipboard(driver.mobile_number, driver.mobile_number)" title="Click to copy">
                   <span class="inline-block group-hover:hidden">
                      {{ driver.mobile_number }}
                   </span>
                   <span class="absolute z-10 top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-teal-100 px-2 py-0.5 rounded shadow whitespace-nowrap opacity-0 scale-95 group-hover:opacity-100 group-hover:scale-100 pointer-events-none group-hover:pointer-events-auto transition-all duration-150 ease-in-out">
                     {{ driver.mobile_number }}
                   </span>
                   <transition name="fade">
                      <span v-if="copiedItemId === driver.mobile_number" class="absolute -top-1 -right-1 bg-teal-200 text-teal-900 text-[10px] font-bold px-1 py-0 rounded-full shadow-md z-20">
                          Copied!
                      </span>
                   </transition>
                </td>
                <td class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200 border-r">{{ driver.car_model }}</td>
                <td class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200 border-r">
                   <span class="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700 text-xs">{{ formatPlateNumber(driver.plate_number) }}</span>
                </td>
                <td v-if="timePeriod === 'daily'" class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200 border-r capitalize">
              {{ driver.screen_name }}
            </td>
                <!-- Use flex layout for number + circle -->
                <td
                  class="py-1.5 px-2 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200 border-r"
                >
                  <div class="flex items-center justify-center space-x-2"> <!-- Flex container -->
                    <!-- Percentage Number -->
                    <span>{{ Math.round(driver.percentage) }} %</span>
                    <!-- Percentage Circle Indicator -->
                    <div
                      class="w-4 h-4 rounded-full flex-shrink-0"
                      :style="getPercentageCircleStyle(driver.percentage)"
                    ></div>
                  </div>
                </td>
                <td class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200 border-r">{{ getUserTime(driver) }}</td>
                <td class="py-3 px-4 whitespace-nowrap text-sm text-gray-700 border-b border-gray-200">
                  <span :class="getLastSeenClass(driver.last_seen)">{{ driver.last_seen }}</span>
                </td>
          </tr>
        </tbody>
      </table>
        </div>
      </Transition>

      <!-- Mobile Card View (Visible only on Mobile) -->
      <Transition name="fade" mode="out-in">
        <div class="block md:hidden py-2">
          <!-- Empty State for Mobile -->
          <div v-if="!loading && !apiError && !processedDrivers.length" class="text-center py-10 text-gray-500">
            <p>No drivers found matching your criteria.</p>
          </div>
          <!-- Mobile Card Loop -->
          <div
            v-if="processedDrivers.length > 0"
            v-for="(driver, index) in processedDrivers"
            :key="driver.id || driver.mobile_number || index"
            class="bg-white rounded-lg shadow p-4 mb-4"
            :class="getRankCardClass(driver.rank)"
          >
            <!-- Card Header: Rank and Name -->
            <div class="flex justify-between items-start mb-3">
               <div class="flex items-center">
                 <span :class="getRankNumberClass(driver.rank)" class="mr-2">
                   #{{ driver.rank }}
                 </span>
                 <h3 class="font-semibold text-lg text-gray-800 mr-2">{{ driver.cruiser_name || 'N/A' }}</h3>
               </div>
               <!-- Last Seen Badge (Mobile) -->
               <span :class="getLastSeenClass(driver.last_seen)">{{ driver.last_seen }}</span>
            </div>

            <!-- Details Grid -->
            <div class="grid grid-cols-2 gap-x-4 gap-y-3 text-sm">
              <!-- Mobile Number -->
              <div class="col-span-1">
                <span class="font-medium text-gray-500 flex items-center mb-0.5">
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" /></svg>
                   Mobile
                </span>
                <div class="flex items-center justify-between">
                   <!-- Apply blue text style -->
                   <span class="text-blue-600 text-sm truncate block mr-2" :title="driver.mobile_number">{{ driver.mobile_number || 'N/A' }}</span>
                   <button
                      v-if="driver.mobile_number"
                      @click="copyToClipboard(driver.mobile_number, driver.mobile_number + '-mobile-btn')"
                      :title="copiedItemId === driver.mobile_number + '-mobile-btn' ? 'Copied!' : 'Copy Mobile'"
                      class="p-1 rounded text-gray-500 hover:bg-gray-100 hover:text-gray-700 transition-colors flex-shrink-0"
                    >
                      <svg v-if="copiedItemId === driver.mobile_number + '-mobile-btn'" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" /></svg>
                      <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                   </button>
                </div>
              </div>

              <!-- Plate Number -->
              <div class="col-span-1">
                <span class="font-medium text-gray-500 flex items-center mb-0.5">
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M10 6H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V8a2 2 0 00-2-2h-5m-4 0V5a2 2 0 114 0v1m-4 0a2 2 0 104 0m-5 8a2 2 0 100-4 2 2 0 000 4zm0 0c1.306 0 2.417.835 2.83 2M9 14a3.001 3.001 0 00-2.83 2M15 11h3m-3 4h2" /></svg>
                   Plate
                </span>
                <span class="text-gray-800 truncate block" :title="driver.plate_number">{{ formatPlateNumber(driver.plate_number) }}</span>
              </div>

               <!-- Car Model -->
              <div class="col-span-1">
                <span class="font-medium text-gray-500 flex items-center mb-0.5">
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>
                   Car Model
                </span>
                <span class="text-gray-800 truncate block" :title="driver.car_model">{{ driver.car_model || 'N/A' }}</span>
              </div>

              <!-- Percentage -->
              <div class="col-span-1">
                <span class="font-medium text-gray-500 flex items-center mb-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M7 12l3-3 3 3 4-4M8 21l4-4 4 4M3 4h18M4 4h16v12a1 1 0 01-1 1H5a1 1 0 01-1-1V4z" /></svg>
                   Percentage
                </span>
                <!-- Add flex container for number + circle -->
                <div class="flex items-center justify-start space-x-2">
                  <span>{{ driver.percentage !== null ? Math.round(driver.percentage) + ' %' : 'N/A' }}</span>
                  <!-- Percentage Circle Indicator -->
                  <div
                    v-if="driver.percentage !== null"
                    class="w-4 h-4 rounded-full flex-shrink-0"
                    :style="getPercentageCircleStyle(driver.percentage)"
                  ></div>
                </div>
              </div>

              <!-- User Time -->
               <div class="col-span-1">
                <span class="font-medium text-gray-500 flex items-center mb-0.5">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
                   User Time
                </span>
                <span class="text-gray-800">{{ getUserTime(driver) }}</span>
              </div>

              <!-- Screen Name (Daily Only) -->
              <div class="col-span-1" v-if="timePeriod === 'daily'">
                <span class="font-medium text-gray-500 flex items-center mb-0.5">
                   <svg xmlns="http://www.w3.org/2000/svg" class="h-4 w-4 mr-1.5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>
                   Screen Name
                </span>
                <span class="text-gray-800 truncate block capitalize" :title="driver.screen_name">{{ driver.screen_name || 'N/A' }}</span>
              </div>

            </div>
         </div>
        </div>
      </Transition>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import VueDatePicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

export default {
  components: { VueDatePicker },
  data() {
    return {
      drivers: [],
      loading: false,
      apiError: false,
      timePeriod: "daily",
      selectedDate: new Date(),
      selectedMonth: { month: new Date().getMonth(), year: new Date().getFullYear() },
      searchQuery: '',
      sortColumn: 'rank',
      sortDirection: 'asc',
      copiedItemId: null,
    };
  },
  computed: {
    processedDrivers() {
      if (!this.drivers) return [];

      let processed = [...this.drivers]; // Create a copy

      // 1. Filtering
      if (this.searchQuery) {
        const lowerQuery = this.searchQuery.toLowerCase();
        // Use the previous filtering logic
        processed = processed.filter(driver => {
           return (
             driver.cruiser_name?.toLowerCase().includes(lowerQuery) ||
             driver.mobile_number?.toLowerCase().includes(lowerQuery) ||
             driver.car_model?.toLowerCase().includes(lowerQuery) ||
             driver.plate_number?.toLowerCase().includes(lowerQuery) ||
             (this.timePeriod === 'daily' && driver.screen_name?.toLowerCase().includes(lowerQuery))
          );
        });
      }

      // 2. Sorting (excluding rank initially)
      const sortKey = this.sortColumn;
      const direction = this.sortDirection === 'asc' ? 1 : -1;

      if (sortKey && sortKey !== 'rank') {
          processed.sort((a, b) => {
              let valA = a[sortKey];
              let valB = b[sortKey];

              // --- Explicit Column Handling ---

              if (sortKey === 'screen_name') {
                  valA = this.extractNumber(a.screen_name);
                  valB = this.extractNumber(b.screen_name);
                  // Perform comparison directly for numbers
                  if (valA < valB) return -1 * direction;
                  if (valA > valB) return 1 * direction;
                  return 0;
              }
               else if (sortKey === 'percentage') {
                  valA = parseFloat(valA) || 0;
                  valB = parseFloat(valB) || 0;
              }
               else if (sortKey === 'user_time' && this.timePeriod === 'mtd'){
                  // Convert MTD formatted string to seconds for comparison
                  valA = (a.user_time_formatted || '').split('h ').reduce((acc, part) => acc * 60 + (parseInt(part.replace('m', '')) || 0), 0);
                  valB = (b.user_time_formatted || '').split('h ').reduce((acc, part) => acc * 60 + (parseInt(part.replace('m', '')) || 0), 0);
              }
               else if (sortKey === 'user_time' && this.timePeriod === 'daily') {
                  // Convert daily formatted string to seconds for comparison
                   valA = this.parseApiTimeStringToSeconds(a.user_time);
                   valB = this.parseApiTimeStringToSeconds(b.user_time);
              }
               else if (sortKey === 'last_seen') {
                  valA = this.parseLastSeen(valA);
                  valB = this.parseLastSeen(valB);
              }
               // Add other specific handlers here if needed

              // --- Generic Handling (if not handled above) ---
               else {
                  // Handle null/undefined before attempting operations
                  if (valA == null && valB == null) return 0;
                  if (valA == null) return -1 * direction; // Sort nulls lower
                  if (valB == null) return 1 * direction;

                  // Generic String comparison
                  if (typeof valA === 'string' && typeof valB === 'string') {
                      valA = valA.toLowerCase();
                      valB = valB.toLowerCase();
                  }
                  // Note: Assumes remaining types are comparable (numbers, etc.)
               }

              // --- Final Comparison (for generic cases) ---
              if (valA < valB) return -1 * direction;
              if (valA > valB) return 1 * direction;
              return 0;
          });
      }

      // 3. Assign Rank based on current order
      processed = processed.map((driver, index) => ({
        ...driver,
        rank: index + 1
      }));

      // 4. Sort by Rank if selected
      if (sortKey === 'rank') {
        processed.sort((a, b) => (a.rank - b.rank) * direction);
      }

      return processed;
    }
  },
  methods: {
    getRankCardClass(rank) {
      // Gradient background for mobile cards - Shorter fade
      if (rank === 1) return 'bg-gradient-to-br from-yellow-100 via-white to-white';
      if (rank === 2) return 'bg-gradient-to-br from-gray-200 via-white to-white';
      if (rank === 3) return 'bg-gradient-to-br from-orange-100 via-white to-white';
      if (rank >= 4 && rank <= 10) return 'bg-gradient-to-br from-teal-100 via-white to-white';
      return ''; // No special background for rank 11+
    },

    getRankNumberClass(rank) {
       // Special styling for top 3 rank numbers in mobile view
       if (rank <= 3) {
         return 'text-xl font-bold text-gray-700 transform -rotate-8 inline-block';
       } else {
         return 'text-sm font-semibold text-gray-500'; // Default style
       }
    },

    getRankRowClass(rank) {
      // Use shorter gradient for ranks 1-10, default for 11+
      if (rank === 1) return 'bg-gradient-to-r from-yellow-100 via-white to-white hover:from-yellow-200';
      if (rank === 2) return 'bg-gradient-to-r from-gray-200 via-white to-white hover:from-gray-300';
      if (rank === 3) return 'bg-gradient-to-r from-orange-100 via-white to-white hover:from-orange-200';
      if (rank >= 4 && rank <= 10) return 'bg-gradient-to-r from-teal-100 via-white to-white hover:from-teal-200';
      return 'bg-white hover:bg-gray-50'; // Default for rank 11+
    },

    async fetchDriversData() {
      console.log("fetchDriversData called. Period:", this.timePeriod, "Date:", this.selectedDate, "Month:", this.selectedMonth); // Debug log
      this.loading = true;
      this.apiError = false;
      try {
        let response;
        if (this.timePeriod === "daily") {
          const apiDate = this.formatDateForAPI(this.selectedDate);
          if (!apiDate) {
            console.error("Invalid date selected for API call");
            this.apiError = true;
            this.loading = false;
            return;
          }
          console.log("Fetching daily for:", apiDate); // Debug log
          response = await axios.get("https://api.dkilo.com/leaderboard", {
            headers: {
              token: "9785|UP3S17pW53LXthsA2OKA5CYR1NL93JBpHaxE77Gf",
              "Country-Code": "SA",
            },
            params: {
              leader_date: apiDate, // Use formatted date
            },
          });
        } else { // Monthly
          const apiMonth = this.formatMonthForAPI(this.selectedMonth);
           if (!apiMonth) {
            console.error("Invalid month selected for API call");
            this.apiError = true;
            this.loading = false;
            return;
          }
          console.log("Fetching monthly for:", apiMonth); // Debug log
          response = await axios.get(
            `https://api.dkilo.com/leaderboard/monthly?month=${apiMonth}` // Use formatted month
          );
        }
        if (response.status === 200) {
          this.drivers = await this.fetchScreenStatusForLeaderBoard(
            response.data
          );
        } else {
          this.apiError = true;
        }
      } catch (error) {
        console.error("Error fetching drivers:", error);
        this.apiError = true;
      } finally {
        this.loading = false;
      }
    },

async fetchScreenStatusForLeaderBoard(leaderBoardData) {
      try {
  let response = await axios.get(
    "https://publish.dkilo.com/api/screen-statuses",
    {}
  );
  if (response.status === 200) {
    const screens = response.data;
    const screenStatusMap = {};
    screens.forEach((screen) => {
      screenStatusMap[screen.screen_id] = {
        online_status: screen.online_status,
        last_offline: screen.last_offline,
      };
    });
    return leaderBoardData.map((driver) => {
      const screenStatus = screenStatusMap[driver.screen_serial];
            let new_last_seen = driver.user_last_seen || "Unknown"; // Start with default
      if (screenStatus) {
        if (screenStatus.online_status === "online") {
                new_last_seen = "Online";
              } else if (screenStatus.last_offline) {
          try {
                  const lastOfflineDate = new Date(screenStatus.last_offline.replace(' ', 'T') + 'Z'); // Assume UTC
            const now = new Date();
            const diffInSeconds = Math.floor((now - lastOfflineDate) / 1000);
            if (diffInSeconds > 0) {
                    new_last_seen = this.formatTimeDifference(diffInSeconds);
            } else {
                    new_last_seen = "Just now"; // Or display formatted last_offline if preferred
            }
          } catch (e) {
                  console.error("Error parsing last_offline date:", screenStatus.last_offline, e);
                  new_last_seen = screenStatus.last_offline || "Unknown time"; // Fallback to raw date string
                }
              }
            }
            return { ...driver, last_seen: new_last_seen };
          });
        }
      } catch (error) {
        console.error("Error fetching screen statuses:", error);
        // Optionally handle this error, e.g., by setting a flag or returning leaderboard data unmodified
      }
      // Return original data if screen status fetch fails or returns non-200
      return leaderBoardData.map(driver => ({ ...driver, last_seen: driver.user_last_seen || "Unknown" }));
    },

    formatTimeDifference(seconds) {
      if (isNaN(seconds) || seconds < 0) return '-';
      if (seconds < 60) return 'just now';
      const minutes = Math.round(seconds / 60);
      const hours = Math.round(seconds / 3600);
      const days = Math.round(seconds / 86400);
      if (minutes < 60) return `${minutes} minute${minutes > 1 ? 's' : ''} ago`;
      if (hours < 24) return `${hours} hour${hours > 1 ? 's' : ''} ago`;
      return `${days} day${days > 1 ? 's' : ''} ago`;
    },

    getUserTime(driver) {
      // console.log("Debugging getUserTime for driver:", JSON.parse(JSON.stringify(driver))); // Log driver data - REMOVED
      if (this.timePeriod === "daily") {
        // Daily view: API returns a formatted string in driver.user_time
        // Format the string before returning, return '-' if it's effectively zero or missing.
        return this.formatApiTimeString(driver.user_time);
      } else {
        // Monthly view: API returns driver.user_time_formatted
        // Return '-' if missing or explicitly "0m"
        return (driver.user_time_formatted && driver.user_time_formatted !== '0m')
          ? driver.user_time_formatted
          : '-';
      }
    },

    formatApiTimeString(timeString) {
      if (!timeString || timeString === "0 hrs 0 min 0 sec") return '-';

      let hours = 0, minutes = 0, seconds = 0;

      const hourMatch = timeString.match(/(\d+)\s*hrs?/);
      const minMatch = timeString.match(/(\d+)\s*min/);
      const secMatch = timeString.match(/(\d+)\s*sec/);

      if (hourMatch) hours = parseInt(hourMatch[1], 10);
      if (minMatch) minutes = parseInt(minMatch[1], 10);
      if (secMatch) seconds = parseInt(secMatch[1], 10);

      const totalSeconds = hours * 3600 + minutes * 60 + seconds;

      if (totalSeconds <= 0) return '-';
      if (totalSeconds < 60) return '< 1m';

      // Recalculate hours and minutes based on total seconds for consistent formatting
      const displayHours = Math.floor(totalSeconds / 3600);
      const displayMinutes = Math.floor((totalSeconds % 3600) / 60);

      let parts = [];
      if (displayHours > 0) parts.push(`${displayHours}h`);
      if (displayMinutes > 0) parts.push(`${displayMinutes}m`);

      // Fallback should ideally not be needed if totalSeconds >= 60
      return parts.length > 0 ? parts.join(" ") : "-";
    },

    formatPlateNumber(plate) {
      if (!plate) return '-';
      const plateStr = String(plate);
      const match = plateStr.match(/^(\d+)\s*([a-zA-Z]+)$|^([a-zA-Z]+)\s*(\d+)$/);
      let numberPart = '-';
      let textPart = '-';
      if (match) {
        if (match[1] && match[2]) { numberPart = match[1]; textPart = match[2].toUpperCase(); }
        else if (match[3] && match[4]) { numberPart = match[4]; textPart = match[3].toUpperCase(); }
        return `${numberPart} ${textPart}`;
      } else {
          const numbers = plateStr.replace(/[^0-9]/g, '');
          const letters = plateStr.replace(/[^a-zA-Z]/g, '').toUpperCase();
          if (numbers && letters) { return `${numbers} ${letters}`; }
          else if (numbers) { return numbers; }
          else if (letters) { return letters; }
      }
      return plateStr.toUpperCase();
    },

    getLastSeenClass(lastSeenText) {
      const baseClasses = 'px-2 py-0.5 inline-flex text-xs leading-5 font-medium rounded-full';
      if (!lastSeenText || typeof lastSeenText !== 'string') return `${baseClasses} bg-gray-100 text-gray-600`; // Default/Unknown

      const lowerText = lastSeenText.toLowerCase();

      if (lowerText === 'online') {
        return `${baseClasses} bg-green-100 text-green-800`; // Green for Online
      }
      if (lowerText === 'just now') {
        return `${baseClasses} bg-yellow-100 text-yellow-800`; // Yellow for Just Now
      }
      if (lowerText.includes('sec') || lowerText.includes('min')) {
        return `${baseClasses} bg-orange-100 text-orange-800`; // Orange for recent offline (sec/min)
      }
      if (lowerText.includes('hour')) {
        return `${baseClasses} bg-red-100 text-red-800`; // Red for hours offline
      }
      if (lowerText.includes('day')) {
        return `${baseClasses} bg-gray-200 text-gray-700`; // Gray for days offline
      }

      return `${baseClasses} bg-gray-100 text-gray-600`; // Default for other cases (raw dates, unknown)
    },

    formatDateForAPI(date) {
      if (!(date instanceof Date)) return null; // Or return today's date as string?
      const year = date.getFullYear();
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const day = date.getDate().toString().padStart(2, '0');
      return `${year}-${month}-${day}`;
    },

    formatMonthForAPI(monthObj) {
      if (!monthObj || typeof monthObj.month !== 'number' || typeof monthObj.year !== 'number') return null;
      const year = monthObj.year;
      const month = (monthObj.month + 1).toString().padStart(2, '0'); // +1 because getMonth is 0-indexed
      return `${year}-${month}`;
    },

    toggleTimePeriod() {
      this.timePeriod = this.timePeriod === 'daily' ? 'mtd' : 'daily';
    },

    formatUserTimeSeconds(seconds) {
      if (seconds === null || seconds === undefined || isNaN(seconds) || seconds <= 0) return "-"; // Return '-' for invalid or zero seconds
      if (seconds < 60) return `< 1m`;
      const hours = Math.floor(seconds / 3600);
      const minutes = Math.floor((seconds % 3600) / 60);
      let parts = [];
      if (hours > 0) parts.push(`${hours}h`);
      if (minutes > 0) parts.push(`${minutes}m`);
      // Removed the extra '0m' cases, handled by initial check
      return parts.length > 0 ? parts.join(" ") : "-"; // Fallback to '-' if calculation somehow yields nothing
    },

    parseLastSeen(lastSeenString) {
      if (!lastSeenString || typeof lastSeenString !== 'string') return Number.MAX_SAFE_INTEGER;
      const lowerString = lastSeenString.toLowerCase();
      if (lowerString === 'online') return -Infinity;
      if (lowerString.includes('unknown')) return Number.MAX_SAFE_INTEGER;
      let secondsAgo = 0;
      const parts = lowerString.replace(' ago', '').split(' ');
      try {
        for (let i = 0; i < parts.length; i += 2) {
          const value = parseInt(parts[i]);
          const unit = parts[i + 1] || '';
          if (isNaN(value)) continue; // Skip if value is not a number
          if (unit.startsWith('day')) secondsAgo += value * 86400;
          else if (unit.startsWith('hr') || unit.startsWith('hour')) secondsAgo += value * 3600;
          else if (unit.startsWith('min')) secondsAgo += value * 60;
          else if (unit.startsWith('sec')) secondsAgo += value;
        }
        return secondsAgo >= 0 ? secondsAgo : Number.MAX_SAFE_INTEGER; // Treat 0 or positive seconds ago
      } catch (e) {
        return Number.MAX_SAFE_INTEGER;
      }
    },

    sortBy(columnKey) {
      const defaultDirection = columnKey === 'rank' ? 'asc' : 'desc';
      if (this.sortColumn === columnKey) {
        this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
      } else {
        this.sortColumn = columnKey;
        this.sortDirection = defaultDirection;
      }
    },

    copyToClipboard(text, id) {
      if (!text) return;
      navigator.clipboard.writeText(text).then(() => {
        console.log('Copied to clipboard:', text, 'with id:', id);
        this.copiedItemId = id;
        setTimeout(() => {
          if (this.copiedItemId === id) {
            this.copiedItemId = null;
          }
        }, 1500);
      }).catch(err => {
        console.error('Failed to copy:', err);
      });
    },

    extractNumber(str) { // Added method from Screens.vue
        if (!str) return Infinity; // Treat null/empty as highest for sorting purposes if needed
        const match = str.match(/-?(\d+)$/); // Match number at the end
        return match ? parseInt(match[1], 10) : Infinity; // Return number or Infinity if no match
    },

    parseApiTimeStringToSeconds(timeString) { // Helper to convert daily string to seconds
      if (!timeString || timeString === "0 hrs 0 min 0 sec") return 0;
      let hours = 0, minutes = 0, seconds = 0;
      const hourMatch = timeString.match(/(\d+)\s*hrs?/);
      const minMatch = timeString.match(/(\d+)\s*min/);
      const secMatch = timeString.match(/(\d+)\s*sec/);
      if (hourMatch) hours = parseInt(hourMatch[1], 10);
      if (minMatch) minutes = parseInt(minMatch[1], 10);
      if (secMatch) seconds = parseInt(secMatch[1], 10);
      return hours * 3600 + minutes * 60 + seconds;
    },

    getPercentageCircleStyle(percentage) {
      const orangeColor = '#FDBA74'; // Tailwind orange-400
      const orangeGlow = '0 0 6px #FDBA74';

      if (percentage > 100) {
        // Over 100% - Solid orange circle with glow
        return {
          background: orangeColor,
          boxShadow: orangeGlow,
          // No border needed here as it's solid
        };
      } else {
        // 100% or less - Conic gradient with border
        const clampedPercent = Math.max(0, Math.min(100, percentage));
        const greenColor = '#A7F3D0'; // Tailwind green-200
        const grayColor = '#E5E7EB'; // Tailwind gray-200
        const borderColor = '#D1D5DB'; // Tailwind gray-300
        return {
          background: `conic-gradient(${greenColor} 0% ${clampedPercent}%, ${grayColor} ${clampedPercent}% 100%)`,
          border: `1px solid ${borderColor}` // Add border for separation
        };
      }
    },

    getPercentageCellStyle(percentage) {
      const clampedPercent = Math.max(0, Math.min(100, percentage));
      const greenColor = '#A7F3D0'; // Tailwind green-200
      const whiteColor = '#FFFFFF'; // Tailwind white
      return {
        background: `linear-gradient(to right, ${greenColor} ${clampedPercent}%, ${whiteColor} ${clampedPercent}%)`
      };
    },

  },

  mounted() {
      console.log("Leaderboard component mounted. Calling fetchDriversData..."); // Debug log
      this.fetchDriversData();
  },

  watch: {
      timePeriod(newVal, oldVal) {
        if (newVal !== oldVal) {
          if (newVal === 'daily') {
            this.selectedMonth = { month: new Date().getMonth(), year: new Date().getFullYear() };
          } else {
            this.selectedDate = new Date();
          }
          this.fetchDriversData();
        }
      },
      selectedDate(newVal, oldVal) {
        // Compare time values for Date objects
        if (newVal instanceof Date && oldVal instanceof Date && newVal.getTime() !== oldVal.getTime() && this.timePeriod === 'daily') {
          this.fetchDriversData();
        }
      },
      selectedMonth(newVal, oldVal) {
        // Compare month and year for month objects
        if (newVal && oldVal && (newVal.month !== oldVal.month || newVal.year !== oldVal.year) && this.timePeriod === 'mtd') {
          this.fetchDriversData();
        }
      }
  }
}
</script>

<style scoped>
.table-container {
  width: 100%;
  overflow-x: auto;
  border-radius: 8px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  margin-top: 20px;
}

/* Fade transition for content */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

/* Added Fade transition for copy notification - Only Fade Out */
.fade-leave-active {
  transition: opacity 1.5s ease;
}

.fade-leave-to {
  opacity: 0;
}

/* Custom styling for vue-datepicker input to match Tailwind */
:deep(.dp-custom-input) {
  padding-top: 0.375rem; /* py-1.5 */
  padding-bottom: 0.375rem; /* py-1.5 */
  padding-left: 1rem; /* px-4 */
  padding-right: 1rem; /* px-4 - adjust if icons overlap */
  border: 1px solid #D1D5DB; /* border-gray-300 */
  border-radius: 1000px; /* rounded-full */
  font-size: 0.875rem; /* text-sm */
  line-height: 1.25rem;
  width: 100%; /* Ensure it fills the w-40 container */
  user-select: none; /* Prevent text selection */
  cursor: pointer; /* Indicate it's clickable */
}

:deep(.dp-custom-input:focus) {
   border-color: #4FD1C5; /* focus:border-primary (adjust color if your primary is different) */
   outline: 2px solid transparent;
   outline-offset: 2px;
   --tw-ring-offset-shadow: var(--tw-ring-inset) 0 0 0 var(--tw-ring-offset-width) var(--tw-ring-offset-color);
   --tw-ring-shadow: var(--tw-ring-inset) 0 0 0 calc(1px + var(--tw-ring-offset-width)) var(--tw-ring-color);
   box-shadow: var(--tw-ring-offset-shadow), var(--tw-ring-shadow), var(--tw-shadow, 0 0 #0000);
   --tw-ring-opacity: 1;
   --tw-ring-color: rgba(79, 209, 197, var(--tw-ring-opacity)); /* focus:ring-primary */
}

/* Apply theme overrides to datepicker */
:deep(.dp__main) {
    --dp-primary-color: #4FD1C5; /* Teal accent */
    --dp-border-radius: 0.375rem; /* rounded-md */
    --dp-cell-border-radius: 0.375rem; /* Rounding for date cells */
    /* Add other overrides as needed, e.g., font sizes, hover colors */
}

/* Override accent color specifically for the monthly picker */
.monthly-picker-wrapper :deep(.dp__main) {
    --dp-primary-color: #3B82F6; /* Blue-500 */
    /* You can override other variables here just for the monthly picker if needed */
}

/* Style selected date like image (dark background) */
:deep(.dp__active_date) {
  background-color: #2D3748; /* Example: dark gray - adjust as needed */
  color: white;
}

/* Style Action buttons (Mimic Apply/Cancel) */
:deep(.dp__action_button) {
  padding: 0.375rem 1rem; /* Adjust padding */
  font-weight: 600; /* semibold */
  border-radius: 0.375rem; /* rounded-md */
}

/* Style Apply button specifically */
:deep(.dp__action_select) {
  background-color: #2D3748; /* Example: dark gray */
  color: white;
  border: 1px solid #2D3748;
}

/* Style Cancel button specifically */
:deep(.dp__action_cancel) {
  background-color: white;
  color: #4A5568; /* Example: gray-700 */
  border: 1px solid #D1D5DB; /* Example: gray-300 */
}
</style>
